import { Box, Paper, Text } from '@mantine/core'
import { useFormat } from '../../_units'
import { useI18n } from '../../_i18n'
import WarningsHead, { warningItems } from './WarningsHead'
import { compassFromDeg, compassLabel, withinSentenceHorizon } from './weatherData'

// The plain-language answer to "what is it like out there right now", sitting
// under the station name.
//
// This is the one part of the page that is PROSE, and that is the point. The
// rest of a station page is labels and numbers — 2,581 characters of visible
// text on a typical page without a single complete sentence — which reads fine
// to a person scanning a table and gives a search engine, a screen reader or an
// assistant nothing to quote. The sentences here are the quotable answer.
//
// Server-rendered, unlike the weather panels: it reads the `marine_conditions`
// prop, derived in Ruby from the ingested slice, so it is in the HTML rather
// than arriving after hydration.
//
// IT DESCRIBES; IT DOES NOT ADVISE. Sea state is the WMO Sea State Code, a
// published standard we can name, rather than a suitability judgement; wind is
// direction, speed and gust with no scale word at all. Nothing on this card
// says an all-clear either, in any state. See Marine::Conditions.
//
// EVERY SENTENCE IS ONE CATALOG STRING with named slots. Never assemble a
// sentence from fragments here: word order, capitalisation and agreement all
// differ across the twelve languages, and a fragment that reads correctly in
// English becomes wrong in German (nouns capitalise) and meaningless in CJK.
// The translator owns the whole sentence.

// Every hazard type mapped explicitly to a catalog key — three reuse the
// existing condition_* strings, the rest are their own.
//
// Deliberately NOT falling back to conditionLabel(), which humanises anything
// it doesn't recognise: that would print a raw code with its underscores
// swapped for spaces, dropping an untranslated English fragment into twelve
// languages. The upstream docs are explicit that hazard types are an open set
// with out-of-vocab values live in the data ("gusts" is one). A code is not a
// word, so an unrecognised type is dropped and the sentence simply omits it.
const HAZARD_KEYS = {
  thunderstorms: 'condition_thunderstorms',
  fog: 'condition_fog',
  rain: 'condition_rain',
  showers: 'hazard_showers',
  reduced_visibility: 'hazard_reduced_visibility',
  haze: 'hazard_haze',
  waterspout: 'hazard_waterspout',
  freezing_spray: 'hazard_freezing_spray',
  ice: 'hazard_ice',
}

function hazardLabel(t, type) {
  const key = type ? HAZARD_KEYS[type] : null
  return key ? t(key) : null
}

// The earliest onset among warnings that have not started yet, as the raw
// instant the formatter wants. Returns null for an empty list.
function earliestOnset(items) {
  let best = null
  for (const item of items) {
    if (best === null || Date.parse(item.starts_at) < Date.parse(best)) best = item.starts_at
  }
  return best
}

export default function ConditionsSummary({ conditions, station, now, kind, timezone, weather }) {
  const { t } = useI18n()
  const fmt = useFormat()

  // The card's head, when there is one. Client-fetched, so it is absent from
  // the server-rendered HTML and appears after hydration; with nothing in
  // force there is no head at all and the card is just the card. Nothing here
  // ever announces an all-clear: 32% of stations have no warning feed, and on
  // the rest a quiet feed is a statement about coverage, not about the water.
  const nowMs = Date.now()
  const head = warningItems(weather, nowMs)

  const water = kind === 'tide' ? tideNow(t, fmt, now, station, timezone) : currentNow(t, fmt, now)

  // The water alone is worth printing. Weather is the enrichment, and it is the
  // half that can be absent (no forecast coverage, a gated-off flag).
  //
  // The head keeps the card alive on its own. A station can carry active
  // warnings from the client fetch while `conditions` is null — the ingest row
  // is missing or stale, or the station is not in the bulk shards — and before
  // the card existed those warnings rendered as their own banner regardless.
  // Returning null here on that combination would silently drop a live NWS
  // warning, which is the worst thing this component could do.
  if (!water && !conditions && head.length === 0) return null

  // THE LEAD SENTENCE ONLY EVER DESCRIBES THIS STATION'S OWN WATER. On the
  // sheltered stations — 4,016 of 12,145, a third of the site — the wave
  // model's cell is wider than the water the station sits in, so its height
  // belongs to the open water in or beyond that cell. Upstream's harbor-problem
  // doc calls printing that number as local truth "the one thing every marine
  // weather API quietly fakes", and measured across the sheltered set the word
  // it would print is calming (smooth or below) on ~2,900 of them — an
  // all-clear about water the model never resolved.
  //
  // So a non-representative sea state stays out of the prose and travels in the
  // NOTE instead, with its height and its origin ("nearby open water: seas
  // around 0.5'"). The reader still gets the number — withholding it entirely
  // left these stations reporting wind and nothing else, under a note
  // referring to open-water values that were not on the page — but as a
  // neighbour's reading, not a claim about here. Describing and attributing
  // are different speech acts, and the card's whole design rests on only ever
  // performing the first one about water we can see.
  const seaCode = conditions?.seas?.representative ? conditions.sea_state?.code : null
  const sea = seaCode ? t(`sea_state_${seaCode}`) : null

  // No station name: the h1 sits directly above this, and the name is already
  // in the title, meta description, breadcrumb, JSON-LD and URL. A seventh
  // occurrence reads as padding, and these names are often survey descriptors
  // rather than places anyone would say aloud ("at Isle au Haut, 0.8 mile E of
  // Rich's Pt").
  //
  // Three frames rather than one with a swapped slot. Beaufort terms are
  // lowercase because they normally sit mid-sentence, so the wind-only frame
  // carries a "Wind:" label rather than leading with one. Sea state is a
  // capitalised scale name and can lead on its own.
  // Direction, speed and gust — no Beaufort word. Beaufort is a SUSTAINED-wind
  // scale that ignores gusts by construction, so it read "a moderate breeze"
  // beside a map showing 22 kt gusts: correct by the scale and misleading at
  // once. It also needed a "Wind:" label to lead a sentence, because its terms
  // are lowercase, and that label fired on 44% of stations — every sheltered
  // one, where the sea state is withheld and the wind stands alone.
  //
  // A compass point capitalises naturally and carries information the summary
  // did not previously show at all. Measured across 3,000 stations, direction
  // is present on every single wind reading; the guard below is for a feed
  // change, not for today.
  // from_compass arrives as the English CODE ("SSE") and is translated here,
  // in the render pass, so the SSR'd sentence localizes with everything else
  // while the service and feed keep emitting codes.
  const direction = conditions?.wind?.from_compass
    ? compassLabel(t, conditions.wind.from_compass)
    : null
  const speed = conditions?.wind?.speed_kt != null ? fmt.wind(conditions.wind.speed_kt) : null
  // The deriver drops a gust that rounds to the sustained speed, so a gust
  // reaching here always means a real difference worth naming.
  const gust = conditions?.wind?.gust_kt != null ? fmt.wind(conditions.wind.gust_kt) : null

  const lead = !(speed && direction)
    ? sea
      ? t('marine_summary_sea_only', { sea })
      : null
    : sea
      ? gust
        ? t('marine_summary_sea_wind_gusting', { sea, direction, speed, gust })
        : t('marine_summary_sea_wind', { sea, direction, speed })
      : gust
        ? t('marine_summary_wind_only_gusting', { direction, speed, gust })
        : t('marine_summary_wind_only', { direction, speed })

  // AN ACTIVE WARNING LEADS. Everything else here describes the gridded hourly
  // model, which cannot resolve convection: at South Santee River the model's
  // hour-mean was 6 kt and "smooth water" while an NWS Special Marine Warning
  // in force read "HAZARD...Wind gusts to 40 knots". The number was right for
  // the hour and irrelevant to the situation, and stating it first read as an
  // all-clear.
  //
  // This matters more here than in the head above, because the head is
  // client-fetched and is therefore absent from the server-rendered HTML. A
  // crawler, an assistant or a screen reader sees only this paragraph.
  //
  // The type is not named and the official text is never paraphrased — the
  // head carries both verbatim. This says only that one exists, which is the
  // fact that must not be missing.
  //
  // IT IS ALWAYS RENDERED, including when the head is on screen saying the same
  // thing in a badge. Dropping it there looked like the right trade and was
  // not: the caveat below exists BECAUSE a warning is in force, and with the
  // sentence gone it read as a disclaimer that had wandered in from nowhere.
  // "The tide is 0.6' and rising: next high 1.3' at 11:18 AM. Tide predictions
  // are astronomical and do not include weather" gives a reader no idea why
  // they are being told the second part. The sentence is what the caveat points
  // back at, so the mild repetition against the badge is the price of the
  // paragraph making sense on its own — which is also the only form a crawler,
  // an assistant or a screen reader ever sees.
  //
  // THE LIVE FEED WINS OVER THE INGESTED SLICE. `conditions` comes from the
  // hourly bulk shards, the head from a live per-station fetch, and the two
  // disagree: diffing one hour of drift across all 12,145 stations found 49
  // disagreements, 46 of them the dangerous way round (live has a warning, the
  // ingest does not). Deriving this from the ingest alone put a red Special
  // Marine Warning in the head directly above "Smooth water, S wind at 6 kt"
  // in the body of the same card — which is exactly the reading the
  // suppression below exists to prevent, now with the contradiction visible in
  // one object. The ingest is the fallback for when the fetch is gated off,
  // still loading or failed, which is also the SSR pass.
  //
  // ISSUED IS NOT IN FORCE. Neither path reads `status` as a statement about
  // the present: it goes true the moment an alert is issued, which is NWS's own
  // semantic and covers an onset days away. Both split it by each warning's own
  // window instead — the live path via warningPhase over the fetched items, the
  // ingested path via Marine::Conditions over the shard's `items`, which the
  // feed gained in marine-weather #75. Same rule, same 12-hour horizon.
  //
  // A shard built before that feed change has no `items` and reports null here,
  // which is NOT the same as reporting zero. Null means "cannot tell" and falls
  // back to reading `status` as before; zero is a real answer from a station
  // with nothing warned. That distinction is the whole reason the two deploys
  // can land in either order.
  const liveStatus = weather?.status === 'ready' ? weather.data?.warnings?.status : null
  const shardPhased = conditions?.warnings_in_force != null

  // The badge above shows every upcoming warning; the sentence speaks only for
  // the ones near enough to be about now. An advisory two days out is real and
  // is badged, but it should not open a paragraph describing this morning.
  const soon = head.filter(
    (item) => item.phase === 'upcoming' && withinSentenceHorizon(item, nowMs),
  )

  const phased = liveStatus
    ? {
        inForce: head.filter((item) => item.phase === 'in_force').length,
        upcoming: soon.length,
        // The head is sorted by severity, so the earliest onset has to be picked
        // out rather than read off the front.
        nextOnsetAt: earliestOnset(soon),
      }
    : shardPhased
      ? {
          inForce: conditions.warnings_in_force,
          upcoming: conditions.warnings_upcoming || 0,
          nextOnsetAt: conditions.next_warning_at,
        }
      : null

  const warningsActive = phased ? phased.inForce > 0 : conditions?.warning_status === 'active'
  const warningCount = warningsActive
    ? phased
      ? phased.inForce
      : conditions?.warning_count || 1
    : 0
  const warning = warningsActive
    ? t(warningCount > 1 ? 'marine_warnings_active' : 'marine_warning_active')
    : null

  // A warning that has been issued but has not started yet gets a sentence of
  // its own, naming when it takes effect. It does not displace the in-force
  // sentence: when both exist the head badges both and the paragraph leads with
  // what is happening now, because that is the one a reader acts on.
  //
  // The time is a full date-and-time, not a bare clock time. The horizon is 12
  // hours, so an onset routinely lands on tomorrow's date, and "takes effect at
  // 6:00 AM" read at 11 PM is ambiguous about which morning is meant. This is
  // the same formatter the badge's window uses directly above.
  const upcomingLine =
    !warningsActive && phased?.upcoming > 0 && phased.nextOnsetAt
      ? t(phased.upcoming > 1 ? 'marine_warnings_upcoming' : 'marine_warning_upcoming', {
          time: fmt.dateTime(phased.nextOnsetAt, timezone),
        })
      : null

  // Under a warning IN FORCE NOW we stop describing the sea and the wind
  // entirely. Those come from the gridded hourly model, which cannot resolve
  // convection:
  // South Santee River read "smooth water, 6 kt" beneath an NWS warning quoting
  // 40-knot gusts. The number was right for the hour and useless for the
  // moment, and a calm reading printed under a warning does more harm than
  // printing nothing. The official text says what is happening; we point at it
  // and stay out of the way.
  //
  // That reasoning is about NOW and does not reach an hour the warning has not
  // arrived in yet. A warning starting tonight says nothing about whether this
  // morning's 6 kt is a fair description of this morning, so an upcoming
  // warning badges and gets its sentence while the sea and wind stay. Blanking
  // them on issuance meant a station went quiet about the water for as long as
  // the feed ran ahead of the weather.
  //
  // The tide and current stay, because they are ours and they are harmonic
  // rather than forecast. They do carry their own caveat though: a prediction
  // is the astronomical tide only, and a surge can run a metre above it. That
  // is what our own guide says, and it is worth saying here at exactly the
  // moment it is most likely to be true.
  //
  // The caveat NAMES THE WARNING it depends on ("that warning" / "those
  // warnings"), rather than stating a general truth about harmonics. It is only
  // ever shown under a warning, so a reader meeting it needs to know which
  // weather is meant; a floating "predictions do not include weather" reads as
  // boilerplate that arrived by accident. Singular and plural are separate
  // strings because the agreement is inside the sentence and belongs to the
  // translator, not to a slot.
  const suppressWeather = warningsActive
  const caveatKey =
    kind === 'tide'
      ? warningCount > 1
        ? 'marine_prediction_caveat_tide_many'
        : 'marine_prediction_caveat_tide_one'
      : warningCount > 1
        ? 'marine_prediction_caveat_current_many'
        : 'marine_prediction_caveat_current_one'
  const caveat = suppressWeather ? t(caveatKey) : null

  const first = conditions?.hazards?.[0]
  const hazard = hazardLabel(t, first?.type)
  const hazardLine = hazard
    ? first.active_now
      ? t('marine_summary_hazard_now', { hazard })
      : t('marine_summary_hazard_later', { hazard })
    : null

  // Currents keep a separate "next event" sentence: the phase now and the next
  // turn are two facts, unlike the tide where height and direction fold into
  // one. Tides need no second sentence because the lead already carries both.
  const nextEvent = kind === 'current' ? currentAnchor(t, fmt, station, now, timezone) : null

  // Provenance, reusing the note_* strings already translated in every catalog.
  // Only the stronger of the two: "sheltered water" says what "from nearby open
  // water" says, and says it better, so printing both repeats itself.
  //
  // The note carries the sea value the lead declined to claim, attributed to
  // where it is actually from. One flag used to cover three different
  // situations; the feed's provenance separates them and each gets its own
  // sentence:
  //
  //   point     (2,712) — the station's own cell answered; the cell is just
  //               wider than the harbour inside it. "Nearby open water."
  //   offshore  (1,248) — the model walked 1-7 cells to find water it had
  //               simulated; the value belongs to different water entirely,
  //               and distance_km (plus bearing_deg, once the feed's `source`
  //               block is deployed) says how far away, and which way, it is.
  //   computed_local (56) — computed FOR this water from wind and fetch;
  //               representative is true upstream, so it takes the lead
  //               sentence and the estimated-from-wind note.
  //
  // The old wording ("sheltered water usually sees less than these open-water
  // values") qualified a value the same flag had just removed from the page.
  // The fetch distance is also gone: it spent the card's longest sentence
  // distinguishing a 300 m creek from a 24 km roadstead while the reader could
  // not see the number either way.
  const notes = conditions?.notes || []
  const foreignSea = conditions?.seas != null && conditions.seas.representative === false
  const seaHeight = foreignSea ? fmt.height(null, conditions.seas.height_ft) : null
  const prov = conditions?.seas?.provenance
  // Direction uses the same localized compass vocabulary as the wind sentence
  // (compass_* catalog keys). The bearing is station-to-source, which is
  // already "which way the open water lies from here" — no flipping, unlike
  // wind's from-direction.
  const sourceDir = prov?.bearing_deg != null ? compassFromDeg(t, prov.bearing_deg) : null
  const note = foreignSea
    ? prov?.status === 'offshore' && prov.distance_km != null
      ? sourceDir
        ? t('note_seas_open_water_distant_dir', {
            height: seaHeight,
            distance: fmt.distance(prov.distance_km * 1000),
            direction: sourceDir,
          })
        : t('note_seas_open_water_distant', {
            height: seaHeight,
            distance: fmt.distance(prov.distance_km * 1000),
          })
      : t('note_seas_open_water_nearby', { height: seaHeight })
    : notes.includes('seas_computed_from_wind') && conditions?.seas
      ? t('note_seas_computed_from_wind')
      : notes.includes('seas_from_nearby_open_water') && conditions?.seas
        ? t('note_seas_from_nearby_open_water')
        : null

  // ONE OBJECT. The warnings were a separate bordered Paper above this text,
  // and the two read as unrelated components describing a single situation.
  // They are now a head and a body inside one card.
  //
  // The head carries no tint of its own — the severity badges are the colour,
  // and a red wash behind them shouted the thing they already say. The divider
  // and the badge do the work.
  // The note is a SENTENCE OF THE PARAGRAPH, not a footnote under it. It
  // rendered as a smaller dimmed line for a while, and that styling argued
  // with its content: "waves on the open water nearby are about 1.7'" is part
  // of the answer to "what is it like out there", not a caveat about the
  // answer. Same voice, same type, one paragraph — which is also the only form
  // the quotable-summary premise at the top of this file ever promised.
  //
  // It still sits last, after the hazard, and still drops under a warning in
  // force: suppression removes every model-derived reading from the paragraph,
  // and the nearby-water value is one of them.
  const sentences = [
    warning,
    upcomingLine,
    water,
    nextEvent,
    ...(suppressWeather ? [caveat] : [lead, hazardLine, note]),
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
      {head.length > 0 && (
        <Box
          p="sm"
          style={
            sentences
              ? { borderBottom: '1px solid var(--mantine-color-default-border)' }
              : undefined
          }
        >
          <WarningsHead items={head} timezone={timezone} />
        </Box>
      )}
      {sentences && (
        <Box p="sm">
          <Text fz="1.05rem" lh={1.55}>
            {sentences}
          </Text>
        </Box>
      )}
    </Paper>
  )
}

// Where the water is at this moment, then where it is going. A tide table
// already says high water is at 7:45; it does not say there is 3.2 ft under you
// now and rising, which is what the station is for.
//
// `now` is interpolated server-side by Marine::NowState using the same Rule of
// Twelfths the map snapshot uses, and `next_tide` comes from the same
// predicted_tides path the table below renders, so neither number can disagree
// with what is drawn beside it.
function tideNow(t, fmt, now, station, timezone) {
  const next = station?.next_tide
  if (now?.height_ft == null || !next?.timestamp) return null
  return t(now.rising ? 'marine_tide_rising' : 'marine_tide_falling', {
    height: fmt.height(now.height_cm, now.height_ft),
    next: fmt.height(next.height_cm, next.height_ft),
    time: fmt.time(next.timestamp, timezone),
  })
}

// Currents get phase and speed now; the turn is a separate sentence, because
// "flooding at 1.4 kt" and "next slack at 4:16" are two facts rather than one.
function currentNow(t, fmt, now) {
  if (!now?.phase) return null
  if (now.phase === 'slack') return t('marine_current_now_slack')
  if (now.speed_kt == null) return null
  const speed = fmt.speed(now.speed_kt)
  return now.phase === 'ebb'
    ? t('marine_current_now_ebb', { speed })
    : t('marine_current_now_flood', { speed })
}

function currentAnchor(t, fmt, station, now, timezone) {
  const next = station?.next_current
  if (!next?.timestamp) return null
  const time = fmt.time(next.timestamp, timezone)
  if (next.max_velocity == null) return t('marine_current_slack', { time })

  // A current's "next event" is the peak of whatever it is already doing, so
  // during a flood this said "The current is flooding at 2.8 kt. Next flood
  // 2.9 kt at 8:12 PM" — the same phase announced twice, as though something
  // were about to change. When the phase matches, it is a peak, and saying so
  // is the useful version of the same fact.
  const nextPhase = next.max_velocity < 0 ? 'ebb' : 'flood'
  if (now?.phase === nextPhase) {
    return t('marine_current_peak', { speed: fmt.speed(Math.abs(next.max_velocity)), time })
  }
  // Flood and ebb each get a whole sentence rather than filling a {{kind}} slot
  // from t('flood') / t('ebb'): those are standalone column labels and are
  // capitalised, which produced "Next Ebb 2.6 kt at 2:26 PM" mid-sentence.
  const speed = fmt.speed(Math.abs(next.max_velocity))
  return next.max_velocity < 0
    ? t('marine_current_ebb', { speed, time })
    : t('marine_current_flood', { speed, time })
}
